import type { ReactNode } from 'react';
import { ThemeToggle } from './ThemeToggle';

interface LayoutProps {
  children: ReactNode;
  onToggleShortlist: () => void;
  shortlistCount: number;
  onShowPrivacy: () => void;
}

export function Layout({
  children,
  onToggleShortlist,
  shortlistCount,
  onShowPrivacy,
}: LayoutProps) {
  return (
    <div className="relative flex min-h-screen flex-col bg-gray-50 text-gray-900 transition-colors dark:bg-gray-950 dark:text-gray-100">
      {/* Ambient radial glow */}
      <div className="bg-ambient pointer-events-none fixed inset-0" aria-hidden="true" />

      {/* Dot grid pattern */}
      <div className="bg-grid pointer-events-none fixed inset-0" aria-hidden="true" />

      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-gray-200/60 bg-white/70 backdrop-blur-xl dark:border-gray-800/60 dark:bg-gray-950/70">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3 sm:px-6">
          {/* Logo */}
          <h1 className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-sky-500 to-violet-500 shadow-sm shadow-sky-500/25">
              <span
                className="font-mono text-xs font-bold leading-none text-white"
                aria-hidden="true"
              >
                DT
              </span>
            </div>
            <span className="font-display text-xl tracking-tight text-gray-900 dark:text-white">
              Domain Triage
            </span>
          </h1>

          {/* Navigation */}
          <nav className="flex items-center gap-1" aria-label="Main navigation">
            <ThemeToggle />

            <button
              type="button"
              onClick={onToggleShortlist}
              aria-label={`Shortlist (${shortlistCount} items)`}
              className="relative rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-5 w-5"
                aria-hidden="true"
              >
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
              {shortlistCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-sky-500 px-1 text-[10px] font-bold leading-none text-white shadow-sm">
                  {shortlistCount}
                </span>
              )}
            </button>


          </nav>
        </div>
      </header>

      {/* Main content */}
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-8 sm:px-6 sm:py-12">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200/60 dark:border-gray-800/60">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-3 px-4 py-6 sm:flex-row sm:justify-between sm:px-6">
          <span className="font-display text-sm text-gray-400 dark:text-gray-600">
            Domain Triage
          </span>
          <div className="flex items-center gap-4 text-sm">
            <button
              type="button"
              onClick={onShowPrivacy}
              className="text-gray-400 transition-colors hover:text-gray-600 focus:outline-none focus-visible:underline dark:text-gray-500 dark:hover:text-gray-300"
            >
              Privacy
            </button>
            <span className="text-gray-300 dark:text-gray-700" aria-hidden="true">
              &middot;
            </span>
            <a
              href="https://mythicworks.net"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 transition-colors hover:text-gray-600 focus:outline-none focus-visible:underline dark:text-gray-500 dark:hover:text-gray-300"
            >
              Mythic Works LLC
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
